import { HYDRATE } from 'next-redux-wrapper';
import { createSelector } from 'reselect';
import { ParsedUrlQuery } from 'querystring';
import { NextRouter } from 'next/router';

import { RootState, ThunkAction } from '../store';
import { SearchPageType, SearchSource } from '../search/SearchContext';
import { updateResults } from '../search/helpers';
import { filtersValuesSelector, resetFilters } from './filters';

const UPDATE_TOTAL_RESULTS = 'searchResults/UPDATE_TOTAL_RESULTS';
const UPDATE_PAGE_NUMBER = 'searchResults/UPDATE_PAGE_NUMBER';
const UPDATE_TOTAL_RESULTS_WAS_LIMITED =
    'searchResults/UPDATE_TOTAL_RESULTS_WAS_LIMITED';
const UPDATE_POLYENC = 'searchResults/UPDATE_POLYENC';

// Types
interface UpdateTotalResultsAction {
    type: typeof UPDATE_TOTAL_RESULTS;
    totalResults: number;
}
interface UpdatePageNumberAction {
    type: typeof UPDATE_PAGE_NUMBER;
    pageNumber: number;
}
interface UpdateTotalResultsWasLimitedAction {
    type: typeof UPDATE_TOTAL_RESULTS_WAS_LIMITED;
    totalResultsWasLimited: boolean;
}
interface UpdatePolyencAction {
    type: typeof UPDATE_POLYENC;
    polyenc: string | null;
}
interface HydrateFiltersAction {
    type: typeof HYDRATE;
    payload: RootState;
}
type SearchResultsActionTypes =
    | UpdateTotalResultsAction
    | UpdatePageNumberAction
    | UpdateTotalResultsWasLimitedAction
    | UpdatePolyencAction
    | HydrateFiltersAction;

export type SearchResultsState = {
    totalResults: number;
    pageNumber: number;
    totalResultsWasLimited: boolean;
    polyenc: string | null;
};

// Actions
export const updateTotalResults = (
    totalResults: number
): UpdateTotalResultsAction => ({
    type: UPDATE_TOTAL_RESULTS,
    totalResults
});
export const updatePageNumber = (
    pageNumber: number
): UpdatePageNumberAction => ({
    type: UPDATE_PAGE_NUMBER,
    pageNumber
});
export const updateTotalResultsWasLimited = (
    totalResultsWasLimited: boolean
): UpdateTotalResultsWasLimitedAction => ({
    type: UPDATE_TOTAL_RESULTS_WAS_LIMITED,
    totalResultsWasLimited
});
export const updatePolyenc = (polyenc: string | null): UpdatePolyencAction => ({
    type: UPDATE_POLYENC,
    polyenc
});

// Reducer
const initialState: SearchResultsState = {
    totalResults: 0,
    pageNumber: 0,
    totalResultsWasLimited: false,
    polyenc: null
};

export const searchResults = (
    state = initialState,
    action: SearchResultsActionTypes
): SearchResultsState => {
    switch (action.type) {
        case HYDRATE: {
            return {
                ...state,
                ...action.payload.searchResults
            };
        }
        case UPDATE_TOTAL_RESULTS: {
            const { totalResults } = action;

            return {
                ...state,
                totalResults
            };
        }
        case UPDATE_PAGE_NUMBER: {
            const { pageNumber } = action;

            return {
                ...state,
                pageNumber
            };
        }
        case UPDATE_TOTAL_RESULTS_WAS_LIMITED: {
            const { totalResultsWasLimited } = action;

            return {
                ...state,
                totalResultsWasLimited
            };
        }
        case UPDATE_POLYENC: {
            const { polyenc } = action;
            return {
                ...state,
                polyenc
            };
        }
        default:
            return {
                ...state
            };
    }
};

// Selectors
const selectSearchResults = (state: RootState): SearchResultsState =>
    state.searchResults;

export const selectTotalResults = (state: RootState) =>
    selectSearchResults(state).totalResults;
export const selectPageNumber = (state: RootState) =>
    selectSearchResults(state).pageNumber;
export const selectTotalResultsWasLimited = (state: RootState) =>
    selectSearchResults(state).totalResultsWasLimited;
export const selectPolyenc = (state: RootState) =>
    selectSearchResults(state).polyenc;

export const totalPagesSelector = createSelector(
    selectTotalResults,
    (totalResults) => {
        const NUMBER_OF_RESULTS_PER_PAGE = 25;

        return Math.ceil(totalResults / NUMBER_OF_RESULTS_PER_PAGE);
    }
);
export const totalResultsTextSelector = createSelector(
    selectTotalResults,
    selectTotalResultsWasLimited,
    (totalResults, totalResultsWasLimited) => {
        if (totalResultsWasLimited) {
            return `${totalResults}+ results`;
        }

        return `${totalResults} results`;
    }
);
export const polyencSelector = createSelector(
    selectPolyenc,
    (polyenc) => polyenc
);

// Action creators
export const resetResultsAsync =
    (
        query: ParsedUrlQuery,
        router: NextRouter,
        searchType: SearchPageType,
        searchSource: SearchSource,
        sortOrder: string,
        userAlertId: string | null = null
    ): ThunkAction =>
    async (dispatch, getState) => {
        await dispatch(resetFilters(undefined, query));

        const store = getState();
        const filters = filtersValuesSelector(store);

        updateResults(
            filters,
            router,
            searchType,
            searchSource,
            sortOrder,
            userAlertId,
            false,
            true
        );
    };

export default searchResults;
